// Mirrors sales.orders / sales.order_items / sales.kitchen_tickets.
import { z } from "zod";

export const deliveryAddressSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: z.string().trim().min(5).max(30),
  street: z.string().trim().min(1).max(200),
  township: z.string().trim().max(80).default(""),
  city: z.string().trim().max(80).default(""),
  note: z.string().trim().max(200).default(""),
});
export type DeliveryAddress = z.infer<typeof deliveryAddressSchema>;

/** What a store offers guests who order from outside it (core.stores.settings.online). Missing values mean: both ways on, no fee, cash only. */
export const onlineSettingsSchema = z.object({
  /** This store sells as an online shop (delivery or pick-up, payment review) instead of table and kitchen ordering. Unset: a shop when the business has no Restaurant module. */
  shop: z.boolean().optional(),
  /** Guests can order from a table by scanning its QR, whether or not the store is also a shop. Unset: on when the business has the Restaurant module. */
  table_orders: z.boolean().optional(),
  delivery: z.boolean().default(true),
  pickup: z.boolean().default(true),
  delivery_fee: z.number().int().min(0).default(0),
  free_over: z.number().int().min(0).default(0),
  cod: z.boolean().default(true),
  transfer: z.boolean().default(false),
  /** The register may take the money for a cash order at any stage. Off: only once it is ready to collect or out for delivery. */
  charge_early: z.boolean().default(true),
});
export type OnlineSettings = z.infer<typeof onlineSettingsSchema>;
/** The delivery fee for an order of this subtotal: free once it reaches `free_over` (when that is set). */
export const deliveryFeeFor = (settings: OnlineSettings, subtotal: number) => (settings.free_over > 0 && subtotal >= settings.free_over ? 0 : settings.delivery_fee);

type ShopOrder = { order_type: string; status: string; payment_status: string; payment_review: string | null; fulfilment: string | null; payment_reject_reason?: string | null };
/**
 * Where an online order stands, for the guest's status page and the staff's next button:
 * rejected (the screenshot didn't match), review (waiting for the store to check it), new (accepted,
 * nothing done yet), packing, out, done, cancelled.
 */
export function shopOrderStage(o: ShopOrder): "rejected" | "review" | "new" | "packing" | "out" | "done" | "cancelled" {
  if (o.status === "cancelled" || o.payment_status === "cancelled" || o.payment_status === "void") return "cancelled";
  if (o.status === "closed") return "done";
  if (o.payment_review === "rejected") return "rejected";
  if (o.payment_review === "pending") return "review";
  return o.fulfilment === "out" ? "out" : o.fulfilment === "packing" ? "packing" : "new";
}

/** An order a guest placed from outside the store (delivery, or pick-up with a chosen way to pay), as opposed to a table or kitchen order. */
export const isShopOrder = (o: { source: string; table_id: string | null; order_type: string; payment_method_name: string | null; payment_proof_key?: string | null }) =>
  o.source === "guest_portal" && !o.table_id && (o.order_type === "online" || !!o.payment_method_name || !!o.payment_proof_key);

export interface ShopOrderStep {
  key: "approve" | "reject" | "packing" | "out" | "done";
  label: string;
  /** The order fields this step writes. "reject" also needs the reason the caller collects. */
  changes: Record<string, unknown>;
}
/** The staff's next buttons for an online order. Cash orders are closed by taking the payment, so "done" is only offered once it's paid. */
export function shopOrderSteps(o: ShopOrder): ShopOrderStep[] {
  const delivery = o.order_type === "online";
  switch (shopOrderStage(o)) {
    case "review":
      return [
        { key: "reject", label: "Doesn't match", changes: { payment_review: "rejected" } },
        { key: "approve", label: "Payment matches", changes: { payment_review: "approved", payment_status: "paid", tender_type: "qr" } },
      ];
    case "new":
      return [{ key: "packing", label: "Start packing", changes: { fulfilment: "packing" } }];
    case "packing":
      return [{ key: "out", label: delivery ? "Out for delivery" : "Ready to collect", changes: { fulfilment: "out" } }];
    case "out":
      return o.payment_status === "paid" ? [{ key: "done", label: delivery ? "Delivered" : "Collected", changes: { status: "closed", completed_at: new Date().toISOString() } }] : [];
    default:
      return [];
  }
}
/**
 * The money on an open online order at the register: whether Charge is offered, and whether charging
 * also closes the order. A screenshot being checked is never charged (approving it pays the order).
 * A rejected one can be: the guest pays at the counter instead. `chargeEarly` is the store's "Take
 * cash at any time" switch.
 */
export function shopCharge(o: ShopOrder, chargeEarly: boolean): { canCharge: boolean; closes: boolean } {
  const stage = shopOrderStage(o);
  const unpaid = o.status === "open" && o.payment_status === "pending" && stage !== "review";
  return { canCharge: unpaid && (chargeEarly || stage === "out" || stage === "rejected"), closes: stage === "out" };
}
export const SHOP_STAGE_LABEL = { rejected: "Waiting for a new screenshot", review: "Check the payment", new: "New order", packing: "Packing", out: "On its way", done: "Done", cancelled: "Cancelled" } as const;

export const orderSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  store_id: z.string().uuid(),
  order_number: z.string(),
  order_type: z.enum(["counter", "dine_in", "pickup", "online"]),
  source: z.enum(["pos", "guest_portal", "mobile_app", "phone", "walk_in"]),
  table_id: z.string().uuid().nullable(),
  customer_name: z.string().nullable(),
  loyalty_member_id: z.string().uuid().nullable(),
  staff_id: z.string().uuid().nullable(),
  device_id: z.string().uuid().nullable(),
  subtotal: z.number().int().default(0),
  discount: z.number().int().default(0),
  discount_source: z.enum(["none", "employee", "promo", "voucher", "manual", "points"]).default("none"),
  employee_discount_percent: z.number().int().min(1).max(100).nullable().default(null),
  tax: z.number().int().default(0),
  rounding_adjustment: z.number().int().default(0),
  total: z.number().int().default(0),
  // Denormalized server-side at commit/append/void-item time (see sync-api's
  // routes/sales.ts) — clients never send this, it's always overwritten.
  cogs: z.number().int().default(0).optional(),
  tender_type: z.enum(["cash", "card", "qr", "wallet", "pending"]).nullable(),
  payment_method_name: z.string().nullable(),
  payment_status: z.enum(["pending", "paid", "refunded", "cancelled", "void"]).default("pending"),
  promo_id: z.string().uuid().nullable(),
  voucher_id: z.string().uuid().nullable(),
  points_earned: z.number().int().default(0),
  points_redeemed: z.number().int().default(0),
  status: z.enum(["open", "closed", "cancelled"]).default("open"),
  notes: z.string().nullable(),
  // An online order that is delivered (order_type "online"): who and where. The fee is the
  // server's, from the store's online settings; pick-up and in-store orders have neither.
  delivery: deliveryAddressSchema.nullable().default(null),
  delivery_fee: z.number().int().min(0).default(0),
  // Paid by transfer: the guest's screenshot (an object key, viewed through a signed link), and
  // what the store made of it. Approving it marks the order paid.
  payment_proof_key: z.string().nullable().default(null),
  payment_review: z.enum(["pending", "approved", "rejected"]).nullable().default(null),
  payment_reject_reason: z.string().nullable().default(null),
  // How far the store has got before handing it over: nothing yet, packing, or out for delivery
  // (ready to collect, for pick-up). The order closing is the hand-over itself.
  fulfilment: z.enum(["packing", "out"]).nullable().default(null),
  refund_reason: z.string().nullable(),
  refunded_at: z.string().nullable(),
  refunded_by_staff_id: z.string().uuid().nullable(),
  // The guest account that placed it from the Guest Portal. Only ever set by sync-api, from the account's own token.
  guest_account_id: z.string().uuid().nullable().default(null),
  completed_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Order = z.infer<typeof orderSchema>;

export const orderItemSchema = z.object({
  id: z.string().uuid(),
  order_id: z.string().uuid(),
  // Denormalized from the parent order — server-computed and always
  // overwritten before insert (same pattern as unit_cost below, hence
  // `.optional()` here rather than required: the client never sends it, and
  // both insert paths — the generic router's beforeInsert and /commit —
  // stamp it in regardless). order_items has no other business_id/store_id
  // column of its own, and Electric's shape `where` clause can't scope
  // reads via a join/subquery to sales.orders (confirmed live: "Subqueries
  // are not supported"), so a direct column is the only way
  // electric-proxy.ts can narrow this table's shape reads per-store.
  store_id: z.string().uuid().optional(),
  product_id: z.string().uuid(),
  variant_id: z.string().uuid().nullable(),
  product_name: z.string(),
  variant_label: z.string().nullable(),
  // Denormalized at insert time from this item's selected options' labels
  // (see sync-api's routes/sales.ts) — clients never send this directly.
  option_labels: z.array(z.string()).default([]).optional(),
  unit_price: z.number().int(),
  // Server-computed at commit time (see sync-api's routes/sales.ts) —
  // clients never send this, it's always overwritten before insert.
  unit_cost: z.number().int().default(0),
  quantity: z.number().int().positive().default(1),
  line_total: z.number().int(),
  is_voided: z.boolean().default(false),
  notes: z.string().nullable(),
  // Which kitchen round this item belongs to — null for an item on an
  // order with no kitchen workflow (a plain retail/service sale). An
  // order stays ONE continuous open bill/tab across multiple rounds of
  // items (see kitchenTicketSchema below); this is what lets a fresh
  // round get its own ticket on the kitchen board without splitting the
  // table's bill in two or reopening an already-served ticket.
  kitchen_ticket_id: z.string().uuid().nullable(),
  // The kitchen works per item: the kitchen display stamps ready_at when one item is done, the
  // till stamps served_at when it is carried out. The ticket's own status follows its items
  // (ready once every item is, done once every item is served).
  ready_at: z.string().nullable().default(null),
  served_at: z.string().nullable().default(null),
  created_at: z.string(),
  updated_at: z.string(),
});
export type OrderItem = z.infer<typeof orderItemSchema>;

// One row per "round" of kitchen prep for an order — introduced so a table
// can keep ordering more onto the SAME open bill without the kitchen ever
// seeing an already-served ticket reappear (mixing done items with brand
// new unprepared ones) or two separate bills for one sitting. An order can
// have several of these over its lifetime (round 1 done, round 2 still
// preparing, ...); order_items.kitchen_ticket_id says which round an item
// belongs to. Same status set orders.kitchen_status used to carry.
export const kitchenTicketSchema = z.object({
  id: z.string().uuid(),
  order_id: z.string().uuid(),
  store_id: z.string().uuid(),
  status: z.enum(["new", "preparing", "ready", "done"]).default("new"),
  done_at: z.string().nullable().default(null),
  // Set by the till's "Running late, nudge kitchen"; the Kitchen Display flags the ticket.
  nudged_at: z.string().nullable().default(null),
  // When cooking began. The database sets it (migration 0071); nothing a device sends changes it.
  started_at: z.string().nullable().default(null),
  created_at: z.string(),
  updated_at: z.string(),
});
export type KitchenTicket = z.infer<typeof kitchenTicketSchema>;

/** A register's end-of-shift cash count (POS "Close the drawer"), shown to the owner under Bills. */
export const drawerCloseSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  device_id: z.string().uuid().nullable().default(null),
  staff_id: z.string().uuid().nullable().default(null),
  opening_float: z.number().int().default(0),
  cash_sales: z.number().int().default(0),
  counted: z.number().int().default(0),
  variance: z.number().int().default(0),
  closed_at: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DrawerClose = z.infer<typeof drawerCloseSchema>;

export const orderItemOptionSelectionSchema = z.object({
  id: z.string().uuid(),
  order_item_id: z.string().uuid(),
  // Same denormalization/scoping reasoning as orderItemSchema.store_id above.
  store_id: z.string().uuid().optional(),
  option_id: z.string().uuid().nullable(),
  add_on_id: z.string().uuid().nullable().default(null),
  selection_type: z.enum(["variant", "add_on", "custom_charge"]).default("variant"),
  quantity: z.number().int().positive().default(1),
  option_label: z.string(),
  price_delta: z.number().int().default(0),
  // Server-computed at commit time, same reasoning as order_items.unit_cost above.
  cost_delta: z.number().int().default(0),
  created_at: z.string(),
});
export type OrderItemOptionSelection = z.infer<typeof orderItemOptionSelectionSchema>;
